import { useCallback, useSyncExternalStore } from "react";
import { dismiss, getSnapshot, promptInstall, subscribe } from "./installPrompt";
import { isIos, isStandalone } from "./platform";

export type InstallPromptState = {
  /** Chrome handed us a deferred prompt — show a real install button. */
  canPrompt: boolean;
  /** No `beforeinstallprompt` exists here; installing has to be explained instead. */
  isIos: boolean;
  /** Already installed — every install surface should render nothing. */
  isStandalone: boolean;
  isDismissed: boolean;
  promptInstall: () => Promise<boolean>;
  dismiss: () => void;
};

/**
 * Reads the module-level install-prompt store. The store exists (rather than an effect
 * in here) because `beforeinstallprompt` is not replayed — see `installPrompt.ts`.
 *
 * `isIos` / `isStandalone` are read once per render rather than subscribed to: neither
 * can change without a page load.
 */
export function useInstallPrompt(): InstallPromptState {
  const { canPrompt, isDismissed } = useSyncExternalStore(subscribe, getSnapshot, getSnapshot);

  return {
    canPrompt,
    isDismissed,
    isIos: isIos(),
    isStandalone: isStandalone(),
    promptInstall: useCallback(() => promptInstall(), []),
    dismiss: useCallback(() => dismiss(), []),
  };
}
